import {
  PaperAirplaneIcon,
  PaperClipIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useRef, useEffect, KeyboardEvent } from 'react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { useToast } from '../../../context/ToastContext';
import { useReceiptQuery } from '../hooks/useReceiptQuery';
import { UploadResult, useUploadFile } from '../hooks/useUploadFile';

const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

interface MessageInputProps {
  onSendMessage: (
    message: string,
    attachmentKey?: string,
    attachmentFileName?: string,
    receiptId?: string
  ) => void;
  disabled?: boolean;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled = false,
}) => {
  const [message, setMessage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const { uploading, progress, error, uploadFile } = useUploadFile();

  const { receipt } = useReceiptQuery(uploadResult?.receiptId ?? null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const SENDABLE_STATUSES = ['ready', 'generated', 'modified', 'imported'];
  const isReceiptReady =
    !!receipt && SENDABLE_STATUSES.includes(receipt.status);
  const isReceiptFailed = receipt?.status === 'text-extraction-failed';

  const handleSend = async (): Promise<void> => {
    if (!canSend) return;

    const messageText = message.trim() || '';
    const attachmentFileName = selectedFile ? selectedFile.name : undefined;
    onSendMessage(
      messageText,
      uploadResult?.storageKey,
      attachmentFileName,
      uploadResult?.receiptId
    );

    // Reset form
    setMessage('');
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.overflowY = 'hidden';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter') {
      if (e.shiftKey) {
        // Allow Shift+Enter for new line
        return;
      } else {
        // Send message on Enter
        e.preventDefault();
        handleSend();
      }
    }
  };

  const handleTextareaChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setMessage(e.target.value);

    // Auto-resize textarea up to 10 lines
    if (textareaRef.current) {
      const textarea = textareaRef.current;

      // Reset height to get accurate scrollHeight
      textarea.style.height = 'auto';

      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 24; // Approximate line height in pixels
      const maxLines = 10;
      const maxHeight = lineHeight * maxLines;

      if (scrollHeight <= maxHeight) {
        // If content fits within max height, expand naturally and hide scrollbar
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      } else {
        // If content exceeds max height, fix height and show scrollbar
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      }
    }
  };

  const handleFocus = (): void => {
    setIsFocused(true);
  };

  const handleBlur = (): void => {
    setIsFocused(false);
  };

  const handleFileSelect = async (file: File): Promise<void> => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      showToast(
        'Invalid File Type',
        'Please select a PDF or image file (JPG, PNG)',
        'warning'
      );
      return;
    }
    setUploadResult(null);
    setSelectedFile(file);
    const result = await uploadFile(file);
    if (result) {
      setUploadResult(result);
    }
  };

  const handleAttachClick = (): void => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveFile = (): void => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag & Drop handlers
  const dragCounter = useRef<number>(0);

  const handleDragEnter = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const canSend: boolean = (() => {
    if (disabled || uploading) return false;
    if (selectedFile) return !!uploadResult && isReceiptReady;
    return message.trim().length > 0;
  })();

  return (
    <div className='bg-white border-t border-gray-200 p-4'>
      <div className='max-w-4xl mx-auto'>
        {/* Main Input Container - Flex Column with Border */}
        <div
          className={`
            relative flex flex-col space-y-3 border rounded-lg p-3 transition-colors
            ${
              isDragOver
                ? 'border-blue-500 bg-blue-50'
                : isFocused
                  ? 'border-blue-500 bg-white'
                  : 'border-gray-300 bg-white'
            }
            ${disabled ? 'bg-gray-100' : ''}
          `}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Textarea Container */}
          <div className='relative'>
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={message}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder='Type your message...'
              disabled={disabled}
              className={`
                w-full px-1 py-2 resize-none border-0 outline-none ring-0 shadow-none
                focus:border-0 focus:outline-none focus:ring-0 focus:shadow-none
                disabled:bg-transparent disabled:cursor-not-allowed
                leading-6 bg-transparent
                ${isDragOver ? 'opacity-30' : 'opacity-100'}
              `}
              style={{
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                minHeight: '24px', // Initial height for one line
                height: 'auto',
                overflowY: 'hidden',
              }}
              rows={1}
            />
          </div>

          {/* Bottom Row: Instructions and Buttons */}
          <div className='flex items-center justify-between'>
            {/* Helper Text */}
            <div className='text-xs text-gray-500 flex-1 pr-4 italic'>
              Enter to send, Shift+Enter for new line
            </div>

            {/* File Info and Button Group */}
            <div className='flex items-center space-x-3'>
              {/* File Info */}
              {selectedFile && (
                <div className='text-xs text-gray-600 flex items-center'>
                  <span>
                    {selectedFile.name} (
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                  {uploading && (
                    <span className='text-blue-600 ml-2'>
                      Uploading {progress}%...
                    </span>
                  )}
                  {error && (
                    <span className='text-red-600 ml-2'>Error: {error}</span>
                  )}
                  {!uploading &&
                    !error &&
                    uploadResult &&
                    !isReceiptReady &&
                    !isReceiptFailed && (
                      <span className='text-amber-600 ml-2 flex items-center'>
                        <LoadingSpinner size='xs' color='border-amber-600' className='mr-1' />
                        Extracting text...
                      </span>
                    )}
                  {!uploading && isReceiptReady && (
                    <span className='text-green-600 ml-2'>Ready</span>
                  )}
                  {!uploading && isReceiptFailed && (
                    <span className='text-red-600 ml-2'>
                      {receipt?.errorMessage || 'Text extraction failed'}
                    </span>
                  )}
                  {!uploading && (
                    <button
                      type='button'
                      onClick={handleRemoveFile}
                      className='ml-2 text-gray-400 hover:text-red-500 transition-colors duration-200'
                      aria-label='Remove file'
                    >
                      <XMarkIcon className='w-3 h-3' />
                    </button>
                  )}
                </div>
              )}

              {/* Button Group */}
              <div className='flex items-center space-x-2'>
                {/* Attach File Button */}
                <button
                  type='button'
                  onClick={handleAttachClick}
                  className={`
                    p-2 rounded-md transition-colors duration-200
                    ${
                      selectedFile
                        ? 'text-blue-600 hover:bg-blue-100'
                        : 'text-gray-500 hover:bg-gray-100'
                    }
                    ${disabled || uploading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  disabled={disabled || uploading}
                  aria-label={
                    selectedFile ? 'Change attached file' : 'Attach file'
                  }
                >
                  <PaperClipIcon className='w-4 h-4' />
                </button>

                {/* Send Button */}
                <button
                  type='button'
                  onClick={handleSend}
                  disabled={!canSend}
                  className={`
                    p-2 rounded-full transition-colors duration-200
                    ${
                      canSend
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-white text-gray-400 border border-gray-300 cursor-not-allowed'
                    }
                  `}
                  aria-label='Send message'
                >
                  {uploading ? (
                    <LoadingSpinner color='border-white' />
                  ) : (
                    <PaperAirplaneIcon className='w-4 h-4' />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Drag Over Overlay */}
          {isDragOver && (
            <div
              className='absolute top-0 left-0 right-0 bottom-0 bg-blue-100 bg-opacity-90 rounded-lg border-2 border-dashed border-blue-500 flex items-center justify-center pointer-events-none z-10'
              style={{ margin: '-0.75rem' }}
            >
              <div className='text-blue-600 font-semibold text-lg flex items-center gap-2'>
                <PaperClipIcon className='w-5 h-5' />
                {selectedFile
                  ? 'Drop file here to replace attached file'
                  : 'Drop file here to attach a file'}
              </div>
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf,.jpg,.jpeg,.png,.gif,image/*,application/pdf'
          onChange={handleFileInputChange}
          className='hidden'
        />
      </div>
    </div>
  );
};

export default MessageInput;
